import React from "react";
import { getStatusTokens } from "@/lib/status";
import { translate, getLocale } from "@/lib/i18n";

// Phase 6.BI-18 — Removed the small leading dot ("· glyph") that used
// to render to the left of the status text. The badge still carries
// status meaning through text + coloured background/border, so the
// dot was visual noise rather than information.
//
// Phase 6.BI-22 — Guard against rendering an EMPTY PILL when `status`
// is null / undefined / "" / whitespace-only. Previously the badge
// always returned the outer `<span>` even when `{status}` rendered as
// nothing, leaving a tiny empty grey rounded pill next to the
// "Technology" / sector badge during loading and on cached watchlist
// stickies that don't carry a fresh status string. The pill came from
// the padding (`px-2.5 py-1`) on an otherwise empty element.
//
// The fix is intentionally permissive about callers — they're allowed
// to pass `{v ? <StatusBadge status={v.status} /> : null}` and the
// component itself handles the case where `v` is present but
// `v.status` isn't resolved yet.
//
// Phase 8.LP.LR v1.6 — Map the internal `data_unavailable` sentinel
// used by GET /api/watchlist to a user-friendly localized label
// ("Not analyzed yet" / "Noch nicht analysiert") with neutral
// tokens. The technical string is NEVER shown to the user.
const INTERNAL_STATUS_LABEL_KEYS = {
  data_unavailable: "watchlist.status_not_analyzed_yet",
  not_analyzed_yet: "watchlist.status_not_analyzed_yet",
};

// Phase 6.4 — canonical classification labels.  Frontend NEVER derives
// the classification; it only maps the persisted value to a
// translated label and visual token.
// Correction Block 2 review follow-up (#3): "neutral" is a legacy
// alias for the canonical "fair" value.  Both map to the same label
// and tokens so cached rows keep rendering correctly during the
// vocabulary migration.
const CLASSIFICATION_LABEL_KEYS = {
  top_opportunity: "analyze.classification.top_opportunity",
  opportunity: "analyze.classification.opportunity",
  fair: "analyze.classification.fair",
  neutral: "analyze.classification.fair",
  risk: "analyze.classification.risk",
  unavailable: "analyze.classification.unavailable",
};
const CLASSIFICATION_TOKENS = {
  top_opportunity: { fg: "#0F3D2E", bg: "linear-gradient(90deg,rgba(63,174,137,0.28),rgba(63,174,137,0.16))", border: "rgba(63,174,137,0.55)" },
  opportunity: { fg: "#0F3D2E", bg: "rgba(63,174,137,0.14)", border: "rgba(63,174,137,0.34)" },
  fair: { fg: "#7A5A00", bg: "rgba(224,179,65,0.14)", border: "rgba(224,179,65,0.34)" },
  neutral: { fg: "#7A5A00", bg: "rgba(224,179,65,0.14)", border: "rgba(224,179,65,0.34)" },
  risk: { fg: "#7A2126", bg: "rgba(229,72,77,0.14)", border: "rgba(229,72,77,0.34)" },
  unavailable: { fg: "#5A5F66", bg: "rgba(168,174,183,0.10)", border: "rgba(168,174,183,0.28)" },
};

export default function StatusBadge({ status, classification, className = "", testId = "stock-status-badge" }) {
  // Phase 6.4 — canonical classification wins when supplied.
  const canon = typeof classification === "string" ? classification.trim() : "";
  if (canon && CLASSIFICATION_LABEL_KEYS[canon]) {
    const key = CLASSIFICATION_LABEL_KEYS[canon];
    const label = translate(key, undefined, getLocale()) || canon;
    const tok = CLASSIFICATION_TOKENS[canon];
    return (
      <span
        data-testid={testId}
        data-classification={canon}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}
        style={{ color: tok.fg, background: tok.bg, border: `1px solid ${tok.border}` }}
      >
        {label}
      </span>
    );
  }

  const raw = typeof status === "string" ? status.trim() : "";
  if (!raw) return null;

  // Translate the small set of internal status sentinels before
  // resolving color tokens so a) the visible text is friendly and
  // b) the badge uses neutral tokens (getStatusTokens falls back to
  // LOADING_STATUS for unknown keys, which is exactly what we want).
  const translationKey = INTERNAL_STATUS_LABEL_KEYS[raw];
  const label = translationKey
    ? translate(translationKey, undefined, getLocale())
    : raw;
  const cfg = getStatusTokens(raw);
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${className}`}
      style={{ color: cfg.fg, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      {label}
    </span>
  );
}
